import { z } from "zod";
/**
 * The standard emission-factor dimension spine, in canonical display order.
 *
 * Every factor table created going forward uses these keys (plus 0–N
 * domain-specific dimensions) rather than inventing synonyms — `region`, never
 * `country`; `year`, never `vintage`. The ordering matches the hardcoded
 * display_order ladder in save_org_emission_factor (20260612133505), promoted
 * here to a contract shared by seeds, meter-type factor bindings, and the
 * utility-emissions engine. `category` is the single required discriminator;
 * the rest are optional per table.
 *
 * `factor_basis` (location_based | market_based) is the Scope-2 dual-reporting
 * dimension. It appears ONLY in factor tables created by the utility-emissions
 * feature and is never added to a pre-existing table — `dimension_values @>`
 * containment means a legacy query omitting the key would match both tagged
 * rows arbitrarily (see 20260724095000).
 */
export const STANDARD_FACTOR_DIMENSIONS = [
    "category",
    "region",
    "year",
    "data_source",
    "lca_scope",
];
/** The Scope-2 basis dimension key + values (utility-emissions tables only). */
export const FACTOR_BASIS_DIMENSION = "factor_basis";
export const FACTOR_BASES = ["location_based", "market_based"];
/**
 * POST /api/v1/emissions/orgs/:orgId/factors
 *
 * Snapshot a looked-up (Climatiq) emission factor into the org's per-sector
 * library. Pattern C → save_org_emission_factor RPC. The RPC derives a
 * deterministic factor_code from (org, table_code, all dimension keys) and is
 * insert-only — a re-pick of the same snapshot identity reuses the existing row.
 */
export const saveEmissionFactorSchema = z
    .object({
    // The discriminators the RPC stores and lookup_emission_factor matches via
    // @>. A generic record so any caller's dimension scheme works — the activity
    // form passes `category` plus optional `region`/`year`/`data_source`/
    // `lca_scope`, each becoming a selectable dimension column. Bounded (key
    // count + key/value length, matching the RPC's own caps) so a misbehaving
    // provider response can't bloat the per-table dimension metadata.
    dimension_values: z
        .record(z.string(), z.union([z.string(), z.number()]))
        .refine((d) => Object.keys(d).length <= 20, {
        message: "too many dimension keys (max 20)",
    })
        .refine((d) => Object.entries(d).every(([k, v]) => k.length <= 100 && String(v).length <= 100), { message: "dimension key/value too long (max 100 chars)" }),
    factor_name: z.string().min(1),
    emission_value: z.number().finite(),
    unit_of_measure: z.string().min(1),
    input_unit_code: z.string().min(1).nullable().optional(),
    output_unit_code: z.string().min(1).nullable().optional(),
    // Emission scope for the saved factor. Omitted ⇒ the RPC defaults to
    // scope_3 (every Climatiq snapshot); custom factors may be scope 1 or 2.
    scope: z.enum(["scope_1", "scope_2", "scope_3"]).nullable().optional(),
    scope_3_category: z.string().min(1).nullable().optional(),
    effective_date: z.string().nullable().optional(),
    external_activity_id: z.string().min(1),
    external_data_version: z.string().min(1).nullable().optional(),
    external_provider: z.string().min(1).optional(),
    // Climatiq taxonomy. `sector` names the per-sector library table
    // ("Climatiq — Transport", …) so the form's table selector is meaningful;
    // `category` is stored on the factor for display. Optional — when absent the
    // RPC falls back to the legacy single 'climatiq_used' library.
    sector: z.string().min(1).nullable().optional(),
    category: z.string().min(1).nullable().optional(),
})
    .strict();
/**
 * POST /api/v1/emissions/admin-factors
 *
 * Super-admin manual factor CRUD on carbon_emission_factors (the admin
 * "Emission Factor Tables" detail page). Pattern B — the backend writes via the
 * per-request client; the RESTRICTIVE verify_backend_token() policy makes this
 * the only allowed write path. `id` present ⇒ update, absent ⇒ insert.
 * System-table factors (organization_id null) are gated to super-admins in the
 * route (RLS doesn't gate that branch by role).
 */
export const adminUpsertEmissionFactorSchema = z
    .object({
    id: z.string().uuid().optional(),
    table_id: z.string().uuid(),
    organization_id: z.string().uuid().nullable(),
    factor_code: z.string().min(1),
    factor_name: z.string().min(1),
    dimension_values: z.record(z.string(), z.unknown()),
    emission_value: z.number().finite(),
    unit_of_measure: z.string().min(1),
    // Typed unit codes behind the legacy unit_of_measure display string —
    // nullable/optional so pre-typed-unit clients and legacy rows still pass.
    input_unit_code: z.string().min(1).nullable().optional(),
    output_unit_code: z.string().min(1).nullable().optional(),
    scope: z.enum(["scope_1", "scope_2", "scope_3"]).nullable().optional(),
    scope_3_category: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    is_active: z.boolean().optional(),
})
    .strict();
/**
 * POST /api/v1/emissions/admin-factor-columns
 *
 * Super-admin / org-member CRUD on `carbon_emission_factor_table_columns` (the
 * admin "Emission Factor Tables" detail page). Pattern B — the backend writes via
 * the per-request client; these tables now carry a RESTRICTIVE
 * verify_backend_token() policy, so this is the only allowed write path. `id`
 * present ⇒ update, absent ⇒ insert. The route derives org/system scope from the
 * target table (body.table_id) and gates system-table writes to super-admins.
 */
export const adminUpsertEmissionFactorColumnSchema = z
    .object({
    id: z.string().uuid().optional(),
    table_id: z.string().uuid(),
    column_name: z.string().min(1),
    column_code: z.string().min(1),
    display_label: z.string().min(1),
    column_type: z.enum(["select", "text", "numeric"]),
    display_order: z.number().int().optional(),
    is_required: z.boolean().optional(),
    description: z.string().nullable().optional(),
})
    .strict();
/**
 * POST /api/v1/emissions/admin-factor-values
 *
 * CRUD on `carbon_emission_factor_column_values`. Pattern B, same RESTRICTIVE
 * gate as columns. `id` present ⇒ update, absent ⇒ insert. The route derives
 * org/system scope from the parent column's table.
 */
export const adminUpsertEmissionFactorColumnValueSchema = z
    .object({
    id: z.string().uuid().optional(),
    column_id: z.string().uuid(),
    value_code: z.string().min(1),
    display_label: z.string().min(1),
    display_order: z.number().int().optional(),
    is_active: z.boolean().optional(),
})
    .strict();
/**
 * One line-item carbon row for the unified transaction-path writer
 * (save_transaction_carbon_results). Mirrors TransactionCarbonRow in
 * apps/web/src/hooks/useCarbonTracking.ts.
 */
export const transactionCarbonRowSchema = z
    .object({
    account_id: z.string().uuid(),
    emission_table_id: z.string().uuid(),
    // Loose on purpose (like adminUpsertEmissionFactorSchema): edit-mode
    // re-saves round-trip STORED rows through this schema, and the column is
    // nullable jsonb with untyped values — a legacy row must not 400 the
    // whole save. The RPC COALESCEs null to '{}' and matches values via @>.
    dimension_values: z.record(z.string(), z.unknown()).nullable(),
    quantity: z.number().finite(),
    quantity_unit: z.string().min(1).nullable(),
    quantity_unit_code: z.string().min(1).nullable(),
    // Quantity pre-converted to the factor's input unit (the multiplier the
    // RPC uses). Null ⇒ the RPC falls back to `quantity`.
    quantity_normalized: z.number().finite().nullable(),
})
    .strict();
/**
 * PUT /api/v1/emissions/orgs/:orgId/carbon-results
 *
 * Full-replace write of a transaction/AP-bill/AR-invoice's line-item carbon
 * rows. Pattern B+ → save_transaction_carbon_results RPC (SECURITY INVOKER,
 * delete-by-key then bulk insert); the RESTRICTIVE verify_backend_token()
 * policy on carbon_emission_results makes this the only allowed
 * transaction-path write. An empty `rows` array clears the entry's carbon.
 *
 * `ar_invoice_id` (20261101120000): an AR draft books nothing, so its carbon
 * keys on the invoice and graduates onto the posting at Issue (the AP-bill
 * precedent). Drafts' rows are excluded from every carbon total.
 */
export const saveTransactionCarbonResultsSchema = z
    .object({
    transaction_id: z.string().uuid().nullable().optional(),
    ap_invoice_id: z.string().uuid().nullable().optional(),
    ar_invoice_id: z.string().uuid().nullable().optional(),
    rows: z.array(transactionCarbonRowSchema).max(200),
})
    .strict()
    .refine((d) => d.transaction_id || d.ap_invoice_id || d.ar_invoice_id, {
    message: "transaction_id, ap_invoice_id or ar_invoice_id is required",
});
/**
 * GET /api/v1/emissions/orgs/:orgId/carbon-results query params. Read — the
 * edit-mode hydration query for the carbon forms. A graduated AP-bill or
 * AR-invoice row carries both keys, so callers may pass any of them; a row
 * matching any one is returned.
 */
export const transactionCarbonResultsQuerySchema = z
    .object({
    transactionId: z.string().uuid().optional(),
    apInvoiceId: z.string().uuid().optional(),
    arInvoiceId: z.string().uuid().optional(),
})
    .strict()
    .refine((d) => d.transactionId || d.apInvoiceId || d.arInvoiceId, {
    message: "transactionId, apInvoiceId or arInvoiceId is required",
});
//# sourceMappingURL=emissions.js.map